import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { db } from '../../firebase';
import { updateDoc, doc } from 'firebase/firestore';
import { 
    Button, 
    Paper, 
    Typography, 
    CircularProgress, 
    Alert, 
    Box,
    Skeleton,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import { 
    fetchAdminsPaginados, 
    fetchUserData, 
    clearUserCache, 
    clearAdminCache 
} from './firestoreService';
import adminProfiler from './utils/AdminPerformanceProfiler';
import "./styles/Config.scss";

const Config = () => {
    const { currentUser, currentService } = useContext(AuthContext);
    const [userData, setUserData] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [showDeletedUsers, setShowDeletedUsers] = useState(false);
    const [users, setUsers] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [lastDoc, setLastDoc] = useState(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [isUsingCache, setIsUsingCache] = useState(false);

    const navigate = useNavigate();

    // Memoizar permisos del usuario para evitar recálculos
    const userPermissions = useMemo(() => ({
        isAdmin: userData?.tipoUsuario?.includes('administrador'),
        isAdministrativo: userData?.tipoUsuario?.includes('administrativo'),
        isInspector: userData?.tipoUsuario?.includes('inspector'),
        userClientes: userData?.clientes || []
    }), [userData?.tipoUsuario, userData?.clientes]);

    // Función optimizada para filtrar usuarios según el rol del usuario actual
    const filterUsersByRole = useCallback((adminsData) => {
        if (!adminsData || adminsData.length === 0) return [];

        const { isAdmin, isAdministrativo, isInspector, userClientes } = userPermissions;

        // Si es administrador, ve todos los usuarios
        if (isAdmin) {
            return adminsData;
        }

        // Crear Set para búsqueda O(1) en lugar de O(n)
        const userClientesSet = new Set(userClientes);

        return adminsData.filter((admin) => {
            const adminRoles = admin.tipoUsuario || [];
            const adminClientes = admin.clientes || [];

            // Si es administrativo, ve inspectores y otros administrativos que comparten clientes
            if (isAdministrativo) {
                const hasValidRole = adminRoles.includes('inspector') || adminRoles.includes('administrativo');
                if (!hasValidRole) return false;
                
                // Verificar si comparten al menos un cliente
                return adminClientes.some(cliente => userClientesSet.has(cliente));
            }

            // Si es inspector, solo ve otros inspectores que comparten clientes
            if (isInspector) {
                if (!adminRoles.includes('inspector')) return false;
                
                // Verificar si comparten al menos un cliente
                return adminClientes.some(cliente => userClientesSet.has(cliente));
            }

            return false;
        });
    }, [userPermissions]);

    // Memoizar usuarios filtrados para evitar recálculos en cada render
    const filteredUsers = useMemo(() => {
        return filterUsersByRole(users);
    }, [users, filterUsersByRole]);

    // Memoizar usuarios finales (con filtro de eliminados)
    const finalUsers = useMemo(() => {
        return filteredUsers.filter(user => showDeletedUsers ? user.eliminado : !user.eliminado);
    }, [filteredUsers, showDeletedUsers]);

    // Cargar datos iniciales en paralelo
    useEffect(() => {
        if (currentUser && currentService) {
            setIsLoading(true);
            setError(null);
            setIsUsingCache(false);

            const loadInitialData = async () => {
                try {
                    // 📊 Iniciar sesión de profiling
                    adminProfiler.startSession('Config', {
                        userId: currentUser?.uid,
                        serviceId: currentService?.id,
                        userEmail: currentUser?.email
                    });

                    const startTime = Date.now();
                    
                    // 📊 Medir carga de userData
                    adminProfiler.startMeasurement('01_FetchUserData', {
                        userId: currentUser.uid
                    });
                    const userDataPromise = fetchUserData(currentUser, currentService);
                    
                    // 📊 Medir carga de admins
                    adminProfiler.startMeasurement('02_FetchAdmins', {
                        pageSize: 50
                    });
                    const adminsPromise = fetchAdminsPaginados(currentService, 50);
                    
                    // Cargar userData y admins en paralelo
                    const [userDataResult, adminsResult] = await Promise.all([
                        userDataPromise,
                        adminsPromise
                    ]);

                    adminProfiler.endMeasurement('01_FetchUserData', {
                        loaded: true,
                        userType: userDataResult?.tipoUsuario
                    });
                    
                    adminProfiler.endMeasurement('02_FetchAdmins', {
                        totalAdmins: adminsResult?.admins?.length || 0,
                        hasMore: adminsResult?.hasMore
                    });

                    const loadTime = Date.now() - startTime;
                    
                    // Detectar si se usó caché (tiempo de carga muy rápido)
                    const usingCache = loadTime < 100;
                    if (usingCache) {
                        setIsUsingCache(true);
                        // Ocultar el indicador de caché después de 3 segundos
                        setTimeout(() => setIsUsingCache(false), 3000);
                    }

                    setUserData(userDataResult);
                    
                    if (adminsResult) {
                        // 📊 Medir filtrado por rol
                        adminProfiler.startMeasurement('03_FilterByRole', {
                            totalAdmins: adminsResult.admins.length,
                            userRole: userDataResult?.tipoUsuario
                        });
                        
                        // Aplicar filtrado una sola vez
                        const filteredUsers = filterUsersByRole(adminsResult.admins);
                        
                        adminProfiler.endMeasurement('03_FilterByRole', {
                            filteredCount: filteredUsers.length,
                            filterRate: `${((filteredUsers.length / adminsResult.admins.length) * 100).toFixed(1)}%`
                        });
                        
                        // 📊 Medir renderizado
                        adminProfiler.startMeasurement('04_RenderUI');
                        
                        setUsers(filteredUsers);
                        setHasMore(adminsResult.hasMore);
                        setLastDoc(adminsResult.lastDoc);
                        
                        // Esperar a que React renderice (next tick)
                        setTimeout(() => {
                            adminProfiler.endMeasurement('04_RenderUI', {
                                usersRendered: filteredUsers.length
                            });
                            
                            // 📊 Finalizar sesión
                            adminProfiler.endSession({
                                totalUsers: filteredUsers.length,
                                usingCache,
                                cacheImprovement: usingCache ? '~90%' : 'N/A',
                                loadTime: `${loadTime}ms`
                            });
                        }, 0);
                    }
                } catch (error) {
                    console.error('Error loading initial data:', error);
                    setError('Error al cargar los datos. Por favor, intenta de nuevo.');
                    
                    // Finalizar sesión con error
                    adminProfiler.endSession({
                        error: error.message,
                        success: false
                    });
                } finally {
                    setIsLoading(false);
                }
            };

            loadInitialData();
        }
    }, [currentUser, currentService, filterUsersByRole]);

    // Cargar más usuarios optimizado
    const loadMoreUsers = useCallback(async () => {
        if (!hasMore || isLoadingMore || !currentService) return;

        setIsLoadingMore(true);
        
        // 📊 Iniciar sesión para carga adicional
        adminProfiler.startSession('Config_LoadMore', {
            serviceId: currentService.id,
            currentUsersCount: users.length
        });
        
        try {
            // 📊 Medir carga de más admins
            adminProfiler.startMeasurement('05_LoadMoreAdmins', {
                pageSize: 50,
                currentPage: Math.floor(users.length / 50) + 1
            });
            
            const result = await fetchAdminsPaginados(currentService, 50, lastDoc);
            
            adminProfiler.endMeasurement('05_LoadMoreAdmins', {
                newAdmins: result?.admins?.length || 0,
                hasMore: result?.hasMore
            });
            
            if (result) {
                // 📊 Medir filtrado
                adminProfiler.startMeasurement('06_FilterNewUsers', {
                    adminsToFilter: result.admins.length
                });
                
                // Aplicar filtrado a los nuevos usuarios
                const newFilteredUsers = filterUsersByRole(result.admins);
                
                adminProfiler.endMeasurement('06_FilterNewUsers', {
                    filteredUsers: newFilteredUsers.length
                });
                
                // Agregar solo los usuarios filtrados
                setUsers(prev => [...prev, ...newFilteredUsers]);
                setHasMore(result.hasMore);
                setLastDoc(result.lastDoc);
                
                // 📊 Finalizar sesión
                adminProfiler.endSession({
                    newUsersAdded: newFilteredUsers.length,
                    totalUsers: users.length + newFilteredUsers.length
                });
            }
        } catch (error) {
            console.error('Error loading more users:', error);
            setError('Error al cargar más usuarios.');
            
            // Finalizar sesión con error
            adminProfiler.endSession({
                error: error.message,
                success: false
            });
        } finally {
            setIsLoadingMore(false);
        }
    }, [hasMore, isLoadingMore, currentService, lastDoc, filterUsersByRole, users.length]);

    // Memoizar funciones de navegación
    const navigateToEditAdmin = useCallback((adminId) => {
        navigate(`/edit-admin/${adminId}`);
    }, [navigate]);

    const navigateToAddUser = useCallback(() => {
        navigate('/add-user');
    }, [navigate]);

    // Función para limpiar caché manualmente
    const handleClearCache = useCallback(() => {
        try {
            clearUserCache(currentService.id);
            clearAdminCache(currentService.id);
            alert('Caché limpiado correctamente. Los datos se recargarán en la próxima visita.');
        } catch (error) {
            console.error('Error clearing cache:', error);
            alert('Error al limpiar el caché.');
        }
    }, [currentService?.id]);

    // Función optimizada para eliminar/recuperar usuario
    const handleDeleteUser = useCallback(async (userId, isDeleted) => {
        if (!userId) {
            console.error('ID de usuario no proporcionado');
            return;
        }

        const confirmationMessage = isDeleted
            ? '¿Estás seguro de que quieres eliminar al usuario?'
            : '¿Estás seguro de que quieres recuperar al usuario?';

        const confirmAction = window.confirm(confirmationMessage);

        if (!confirmAction) {
            return;
        }

        try {
            const userToDelete = users.find(user => user.id === userId);
            const userType = userToDelete?.tipoUsuario || [];

            if (userType.includes('administrador')) {
                alert(`No puedes ${isDeleted ? 'eliminar' : 'recuperar'} a un administrador.`);
                return;
            }

            const userRef = doc(db, 'servicios', currentService.id, 'admins', userId);
            await updateDoc(userRef, { eliminado: isDeleted });

            // Limpiar caché después de actualizar
            clearUserCache(currentService.id, userId);
            clearAdminCache(currentService.id);

            // Actualizar estado local
            setUsers(prevUsers =>
                prevUsers.map(user => (user.id === userId ? { ...user, eliminado: isDeleted } : user))
            );

            alert(`Usuario ${isDeleted ? 'eliminado' : 'recuperado'} correctamente`);
        } catch (error) {
            console.error(`Error al ${isDeleted ? 'eliminar' : 'recuperar'} el usuario:`, error);
            alert(`Error al ${isDeleted ? 'eliminar' : 'recuperar'} el usuario.`);
        }
    }, [users, currentService?.id]);

    // Memoizar función de toggle para usuarios eliminados
    const toggleShowDeletedUsers = useCallback(() => {
        setShowDeletedUsers(prev => !prev);
    }, []);

    if (isLoading) {
        return (
            <div className="config-container">
                <h1 className="usuarios-title">Usuarios</h1>
                <div style={{ padding: '20px' }}>
                    {[...Array(5)].map((_, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                            <Skeleton variant="rectangular" height={120} />
                        </Box>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="config-container">
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button 
                    variant="contained" 
                    onClick={() => window.location.reload()}
                    sx={{ mt: 2 }}
                >
                    Reintentar
                </Button>
            </div>
        );
    }

    return (
        <div className="config-container">
            <h1 className="usuarios-title">Usuarios</h1>
            
            {/* Indicador de caché */}
            {isUsingCache && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        ⚡ Datos cargados desde caché - Carga instantánea
                    </Typography>
                </Alert>
            )}
            
            <div className='buttonAdd'>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showDeletedUsers}
                            onChange={toggleShowDeletedUsers}
                        />
                    }
                    label="Mostrar usuarios eliminados"
                />
                <Button 
                    onClick={navigateToAddUser} 
                    variant="contained" 
                    color="primary" 
                    style={{ margin: '20px' }}
                >
                    Añadir Nuevo Usuario
                </Button>
                <Button 
                    onClick={handleClearCache} 
                    variant="outlined" 
                    color="secondary" 
                    style={{ margin: '20px' }}
                >
                    Limpiar Caché
                </Button>
            </div>

            {finalUsers.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                    {showDeletedUsers ? 'No hay usuarios eliminados.' : 'No hay usuarios disponibles.'}
                </Alert>
            ) : (
                <>
                    {finalUsers.map((user) => (
                        <div className='paperContainer' key={user.id}>
                            <Paper elevation={3} className="user-paper">
                                <Typography variant="h6">
                                    {user.nombre} {user.apellido}
                                </Typography>
                                <Typography variant="body1">
                                    Correo: {user.correo}
                                </Typography>
                                <Typography variant="body2">
                                    Tipo de Usuario: {user.tipoUsuario?.join(', ') || 'Sin tipo'}
                                </Typography>
                                {userPermissions.isAdmin ||
                                    (userPermissions.isAdministrativo && !user?.tipoUsuario?.includes('administrador')) ? (
                                        <Button onClick={() => navigateToEditAdmin(user.id)}>
                                            Editar
                                        </Button>
                                    ) : null}
                                <Button onClick={() => handleDeleteUser(user.id, !user.eliminado)}>
                                    {user.eliminado ? 'Recuperar' : 'Eliminar'}
                                </Button>
                            </Paper>
                        </div>
                    ))}

                    {hasMore && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Button
                                onClick={loadMoreUsers}
                                disabled={isLoadingMore}
                                variant="outlined"
                            >
                                {isLoadingMore ? (
                                    <>
                                        <CircularProgress size={20} sx={{ mr: 1 }} />
                                        Cargando...
                                    </>
                                ) : (
                                    'Cargar más usuarios'
                                )}
                            </Button>
                        </Box>
                    )}
                </>
            )}
        </div>
    );
};

export default Config;

